/*
 * Visits: computed from jobs until one is touched (moved, started, edited, completed), then a row (BUILD-SPEC 4 and 4.1).
 *
 * A visit is known by its ref: the row's id once it exists, or "<job id>.<date>" for a computed one (the date is the day the
 * schedule engine gives it). Touching a computed visit makes its row and keeps that original date, so a moved visit still
 * replaces the computed one on the day it came from, and the job's regular day stays as it was.
 *
 * Pure functions only, so the server, the web screens and the phone app plan the same visits.
 */
import { occurrences, isDue, type ScheduledJob } from "./schedule.js";
import type { Cents } from "./core.js";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export type VisitRef = { kind: "row"; id: string } | { kind: "computed"; jobId: string; date: string };

/** The ref of a computed visit. */
export function computedRef(jobId: string, date: string): string {
  return `${jobId}.${date}`;
}

/** Reads a visit ref from an address. Anything else is null (the server answers "not found"). */
export function parseVisitRef(ref: string): VisitRef | null {
  if (UUID.test(ref)) return { kind: "row", id: ref.toLowerCase() };
  const [jobId, date, more] = ref.split(".");
  if (more !== undefined || !jobId || !date || !UUID.test(jobId) || !DATE.test(date)) return null;
  return { kind: "computed", jobId: jobId.toLowerCase(), date };
}

/** The part of a touched visit the planner needs. */
export interface TouchedVisit {
  id: string;
  jobId: string;
  /** The day the schedule engine gave it, before any move. */
  originalDate: string;
  date: string;
  status: string;
}

export interface PlannedVisit {
  ref: string;
  /** The row's id, or null while it is only computed. */
  id: string | null;
  jobId: string;
  date: string;
  originalDate: string;
  moved: boolean;
  status: string;
}

/**
 * Every visit of one job from `from` to `to`: computed days, with touched visits laid over them by their original date.
 * A visit moved into the range from outside it shows; one moved out of the range does not. Cancelled visits stay in the
 * list (the screen shows them struck through); deleted rows never reach here.
 */
export function planVisits(
  job: ScheduledJob & { id: string },
  touched: readonly TouchedVisit[],
  from: string,
  to: string,
): PlannedVisit[] {
  const mine = touched.filter((t) => t.jobId === job.id);
  const byOriginal = new Map(mine.map((t) => [t.originalDate, t]));
  const out: PlannedVisit[] = [];
  for (const date of occurrences(job, from, to)) {
    if (byOriginal.has(date)) continue;
    out.push({
      ref: computedRef(job.id, date),
      id: null,
      jobId: job.id,
      date,
      originalDate: date,
      moved: false,
      status: "scheduled",
    });
  }
  for (const t of mine) {
    if (t.date < from || t.date > to) continue;
    out.push({
      ref: t.id,
      id: t.id,
      jobId: job.id,
      date: t.date,
      originalDate: t.originalDate,
      moved: t.date !== t.originalDate,
      status: t.status,
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** Is there a visit of this job whose original day is this date (computed or touched)? */
export function isVisitDay(job: ScheduledJob, date: string): boolean {
  return isDue(job, date);
}

/**
 * A job's price line. "every" lines come on every visit; "once" lines only on the visit of their start day (a hedge trim
 * the first time); an extra can have its own repeat (monthly window wash).
 */
export interface PlanLine {
  frequency: string;
  startDate: string | null;
  endsOn: string | null;
  amountCents: Cents;
}

/** The job lines that apply to a visit on this date (the line's own start and end, and its own repeat for extras). */
export function linesOn<T extends PlanLine>(
  lines: readonly T[],
  jobStart: string | null,
  date: string,
  repeatDay: number | null = null,
): T[] {
  return lines.filter((l) => {
    const start = l.startDate ?? jobStart;
    if (start && date < start) return false;
    if (l.endsOn && date > l.endsOn) return false;
    if (l.frequency === "every") return true;
    if (l.frequency === "once") return date === start;
    return isDue({ frequency: l.frequency, startDate: start, repeatDay }, date);
  });
}

/** How often, in the words the screens use. */
export function frequencyLabel(frequency: string): string {
  switch (frequency) {
    case "one-time":
    case "once":
      return "One time";
    case "daily":
      return "Every day";
    case "weekly":
      return "Every week";
    case "biweekly":
      return "Every 2 weeks";
    case "monthly":
      return "Every month";
    case "bimonthly":
      return "Every 2 months";
    case "every":
      return "Every visit";
    default:
      return frequency;
  }
}

/** Cancelled and skipped visits are off the schedule: the old Steward's month never showed them. */
export function isOffSchedule(status: string): boolean {
  return status === "cancelled" || status === "skipped";
}

/**
 * One day's visits as the schedule lists them: by start time (no time last), then by client, like the old Steward.
 * Cancelled and skipped ones only when asked for.
 */
export function dayVisits<T extends { date: string; startTime: string | null; clientName: string; status: string }>(
  visits: readonly T[],
  date: string,
  showOff = false,
): T[] {
  return visits
    .filter((v) => v.date === date && (showOff || !isOffSchedule(v.status)))
    .sort((a, b) => (a.startTime ?? "99").localeCompare(b.startTime ?? "99") || a.clientName.localeCompare(b.clientName));
}
